import { formatNameLastFirst } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast, WarningToast } from '@strata/react-toast-message';
import { Worklist } from '@strata/react-worklist';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { createWorklistTask } from 'api/worklistAPI';
import { handleError } from 'services/Error';
import { useGetProductByOidQuery } from 'store/api/referenceApi';

const Title = 'Create Worklist Task';

const WorklistTaskOptions = [
    {
        value: Worklist.WorkTaskType.NBD,
        name: 'Needs By Date',
        reasons: undefined,
    },
];

export const CreateWorklistTaskDialog = ({
    isOpen,
    onClose,
    referralDetails = undefined,
    needsByDateData = undefined,
}) => {
    const { data: product, isLoading: productLoading } = useGetProductByOidQuery(
        referralDetails?.productServiceOid,
        {
            skip: !referralDetails?.productServiceOid,
        },
    );

    if (productLoading) {
        return <FullscreenLoading loadingText="Loading create worklist dialog..." />;
    }

    return (
        <Formik
            initialValues={{
                worklistTaskType: undefined,
            }}
            validateOnMount
            validate={(values) => {
                const schema = Yup.object({
                    worklistTaskType: Yup.object().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    ...(values.worklistTaskType?.reasons ? ReasonInput.validationSchema : {}),
                });
                try {
                    validateYupSchema(values, schema, true);
                } catch (err) {
                    return yupToFormErrors(err);
                }
                return {};
            }}
            onSubmit={async (values, { resetForm }) => {
                try {
                    const response = await createWorklistTask({
                        supplierNpi: referralDetails.supplierNpi,
                        taskType: values.worklistTaskType.value,
                        body: {
                            ...(values.worklistTaskType?.reasons
                                ? ReasonInput.submitValues(values)
                                : {}),
                            payer_id: referralDetails.payerId,
                            segment_id: referralDetails.segmentId,
                            referral_id: referralDetails.referralId,
                            treatment_id: referralDetails.treatmentId,
                            supplier_npi: referralDetails.supplierNpi,
                            friendly_id: referralDetails.friendlyId,
                            product_name: product?.description,
                            patient_name: formatNameLastFirst(
                                referralDetails.patientLastName,
                                referralDetails.patientFirstName,
                            ),
                            needs_by_date: needsByDateData.needsByDate,
                        },
                    });
                    if (response?.Error) {
                        WarningToast({ message: response.Error });
                    } else {
                        SuccessToast({ message: 'Worklist task created successfully.' });
                    }
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to create worklist task. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Create"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="worklistTaskType"
                                label="Worklist Task"
                                values={WorklistTaskOptions}
                                required
                            />
                            {formik.values.worklistTaskType?.reasons && (
                                <ReasonInput reasons={formik.values.worklistTaskType?.reasons} />
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

CreateWorklistTaskDialog.Title = Title;

CreateWorklistTaskDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    referralDetails: PropTypes.shape({
        treatmentId: PropTypes.string,
        referralId: PropTypes.string,
        friendlyId: PropTypes.string,
        payerId: PropTypes.string,
        segmentId: PropTypes.string,
        productServiceOid: PropTypes.string,
        patientFirstName: PropTypes.string,
        patientLastName: PropTypes.string,
        supplierNpi: PropTypes.string,
    }),
    needsByDateData: PropTypes.shape({
        needsByDate: PropTypes.string,
    }),
};
